/**
 * Shipment Wizard Component
 * MPAD Implementation: Phase 2 - Progressive Disclosure Shipment Creation
 * 
 * Multi-step wizard for creating shipments with pickup-delivery pairs,
 * following Maya's UX patterns for complex form management.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Chip,
  Autocomplete,
  Slider,
  FormControlLabel,
  Switch,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  InputAdornment,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  LocalShipping,
  Schedule,
  Inventory2 as Inventory,
  NavigateNext,
  NavigateBefore,
  Check,
  Warning,
  Info,
  Place,
  AccessTime,
  Speed,
  DirectionsCar,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useLocations } from '../../hooks/useLocations';
import type { Location } from '../../types/vrp';
import { useProducts, type Product as ProductType } from '../../hooks/useProducts';
import { useSkills, type Skill as SkillType } from '../../hooks/useSkills';

// ===============================================
// TYPES AND INTERFACES
// ===============================================

// Use real Location type from database - removed local interface

// Use Product type from useProducts hook
type Product = ProductType;

// Use Skill type from useSkills hook but map field names
interface Skill {
  id: number;
  name: string; // Maps to skill_name from SkillType
  description?: string;
  skill_code?: string;
  category?: string;
  subcategory?: string;
  skill_type?: string;
  requires_certification: boolean;
  certification_authority?: string;
  vroom_skill_id?: number;
}

interface ProductAssignment {
  product_id: number;
  product?: Product;
  quantity: number;
  loading_sequence?: number;
  special_instructions?: string;
}

interface SkillRequirement {
  skill_id: number;
  skill?: Skill;
  minimum_proficiency_level: number;
  is_mandatory: boolean;
  reasoning?: string;
}

interface ShipmentFormData {
  // Basic Info
  shipment_reference: string;
  description?: string;
  priority: number;
  
  // Locations
  pickup_location_id?: number;
  delivery_location_id?: number;
  
  // Enhanced Time Windows (Date + Time)
  pickup_time_window_start_dt?: Date;
  pickup_time_window_end_dt?: Date;
  delivery_time_window_start_dt?: Date;
  delivery_time_window_end_dt?: Date;
  
  // Legacy Time Windows (backward compatibility)
  pickup_time_window_start: number;
  pickup_time_window_end: number;
  delivery_time_window_start: number;
  delivery_time_window_end: number;
  
  // Setup and Service Times (minutes)
  pickup_setup: number;
  pickup_service: number;
  delivery_setup: number;
  delivery_service: number;
  
  // Products
  products: ProductAssignment[];
  
  // Skills Requirements
  required_skills: SkillRequirement[];
  
  // Constraints
  is_direct_delivery: boolean;
  requires_same_vehicle: boolean;
  max_transit_time?: number;
}

interface ShipmentWizardProps {
  projectId: number;
  onSave: (shipment: ShipmentFormData) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<ShipmentFormData>;
  mode?: 'create' | 'edit';
}

type WizardStep = 'basic' | 'locations' | 'time_windows' | 'products' | 'skills' | 'constraints' | 'review';

const WIZARD_STEPS: { id: WizardStep; label: string; icon: React.ReactNode }[] = [
  { id: 'basic', label: 'Basic Info', icon: <Info /> },
  { id: 'locations', label: 'Locations', icon: <Place /> },
  { id: 'time_windows', label: 'Time Windows', icon: <Schedule /> },
  { id: 'products', label: 'Products', icon: <Inventory /> },
  { id: 'skills', label: 'Skills & Requirements', icon: <Speed /> },
  { id: 'constraints', label: 'Constraints', icon: <DirectionsCar /> },
  { id: 'review', label: 'Review', icon: <Check /> }
];

// ===============================================
// VALIDATION SCHEMA
// ===============================================

const shipmentValidationSchema = z.object({
  shipment_reference: z.string()
    .min(1, 'Shipment reference is required')
    .max(100, 'Reference must be less than 100 characters'),
  
  description: z.string()
    .max(1000, 'Description must be less than 1000 characters')
    .nullable()
    .optional(),
  
  priority: z.number()
    .min(1, 'Priority must be at least 1')
    .max(10, 'Priority must be at most 10'),
  
  pickup_location_id: z.number()
    .positive('Invalid pickup location'),
  
  delivery_location_id: z.number()
    .positive('Invalid delivery location'),
  
  // Enhanced DateTime validation
  pickup_time_window_start_dt: z.date()
    .optional(),
  pickup_time_window_end_dt: z.date()
    .optional(),
  delivery_time_window_start_dt: z.date()
    .optional(),
  delivery_time_window_end_dt: z.date()
    .optional(),
  
  // Legacy time windows (backward compatibility)
  pickup_time_window_start: z.number()
    .min(0, 'Start time must be at least 0')
    .max(1440, 'Start time must be at most 1440'),
  
  pickup_time_window_end: z.number()
    .min(0, 'End time must be at least 0')
    .max(1440, 'End time must be at most 1440'),
  
  delivery_time_window_start: z.number()
    .min(0, 'Start time must be at least 0')
    .max(1440, 'Start time must be at most 1440'),
  
  delivery_time_window_end: z.number()
    .min(0, 'End time must be at least 0')
    .max(1440, 'End time must be at most 1440'),
  
  // Setup and Service Times validation
  pickup_setup: z.number()
    .min(0, 'Setup time must be at least 0 minutes')
    .max(480, 'Setup time must be at most 8 hours'),
  
  pickup_service: z.number()
    .min(0, 'Service time must be at least 0 minutes')
    .max(480, 'Service time must be at most 8 hours'),
  
  delivery_setup: z.number()
    .min(0, 'Setup time must be at least 0 minutes')
    .max(480, 'Setup time must be at most 8 hours'),
  
  delivery_service: z.number()
    .min(0, 'Service time must be at least 0 minutes')
    .max(480, 'Service time must be at most 8 hours'),
  
  products: z.array(
    z.object({
      product_id: z.number(),
      product: z.any().optional(),
      quantity: z.number().positive(),
      loading_sequence: z.number().optional(),
      special_instructions: z.string().optional()
    })
  ).min(1, 'At least one product is required'),
  
  // Skills validation
  required_skills: z.array(
    z.object({
      skill_id: z.number(),
      skill: z.any().optional(),
      minimum_proficiency_level: z.number().min(1).max(10),
      is_mandatory: z.boolean(),
      reasoning: z.string().optional()
    })
  ).optional(),
  
  max_transit_time: z.number()
    .positive('Transit time must be positive')
    .nullable()
    .optional(),
    
  is_direct_delivery: z.boolean(),
  requires_same_vehicle: z.boolean()
}).refine((data) => {
  // Custom validation for delivery location different from pickup
  return data.delivery_location_id !== data.pickup_location_id;
}, {
  message: 'Delivery location must be different from pickup',
  path: ['delivery_location_id']
}).refine((data) => {
  // Custom validation for pickup time window (DateTime)
  if (data.pickup_time_window_start_dt && data.pickup_time_window_end_dt) {
    return data.pickup_time_window_end_dt > data.pickup_time_window_start_dt;
  }
  // Fallback to legacy validation
  return data.pickup_time_window_end > data.pickup_time_window_start;
}, {
  message: 'End time must be after start time',
  path: ['pickup_time_window_end_dt']
}).refine((data) => {
  // Custom validation for delivery time window (DateTime)
  if (data.delivery_time_window_start_dt && data.delivery_time_window_end_dt) {
    return data.delivery_time_window_end_dt > data.delivery_time_window_start_dt;
  }
  // Fallback to legacy validation
  return data.delivery_time_window_end > data.delivery_time_window_start;
}, {
  message: 'End time must be after start time',
  path: ['delivery_time_window_end_dt']
});

// ===============================================
// REAL DATA INTEGRATION
// ===============================================

// ===============================================
// UTILITY FUNCTIONS
// ===============================================

const formatTimeWindow = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const getTimeMarks = () => {
  return [
    { value: 0, label: '00:00' },
    { value: 360, label: '06:00' },
    { value: 720, label: '12:00' },
    { value: 1080, label: '18:00' },
    { value: 1440, label: '24:00' }
  ];
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const ShipmentWizard: React.FC<ShipmentWizardProps> = ({
  projectId,
  onSave,
  onCancel,
  initialData,
  mode = 'create'
}) => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdShipment, setCreatedShipment] = useState<any>(null);
  
  // Get scenario and dataset context
  // Unified VRP architecture - get scenario and dataset context
  const { currentScenario, currentDataset } = null /* TODO: Replace with Toolpad navigation */;
  
  // Navigation hook for success actions
  const navigate = useNavigate();
  
  // Real data hooks
  const { data: productsResponse = { products: [], total_count: 0 }, isLoading: productsLoading, error: productsError } = useProducts(projectId);
  const { data: skillsResponse = [], isLoading: skillsLoading, error: skillsError } = useSkills(projectId);
  
  // Extract actual arrays from response objects
  const products = productsResponse.products || [];
  const skills = useMemo(() => {
    // Map skills from the Edge Function response
    return (skillsResponse || []).map((skill: any) => ({
      id: skill.id,
      name: skill.name || skill.skill_name, // Support both field names
      description: skill.description,
      skill_code: skill.skill_code,
      category: skill.category,
      subcategory: skill.subcategory,
      skill_type: skill.skill_type,
      requires_certification: skill.requires_certification,
      certification_authority: skill.certification_authority,
      vroom_skill_id: skill.vroom_skill_id
    }));
  }, [skillsResponse]);
  
  // Use real locations from database with scenario context
  // If no scenario is selected, load all locations for the project
  const { data: locations = [], isLoading: locationsLoading } = useLocations(
    projectId, 
    currentScenario?.id || undefined,  // Pass undefined instead of null/0 to load all locations
    {
      dataset_id: currentDataset?.id,
      enabled: !!projectId  // Only fetch if we have a project ID
    }
  );
  
  // Filter locations with valid coordinates for shipments
  const validLocations = useMemo(() => {
    return locations.filter(location => 
      location.location_lon && 
      location.location_lat &&
      typeof location.location_lon === 'number' &&
      typeof location.location_lat === 'number'
    );
  }, [locations]);

  // Debug logging
  useEffect(() => {
    console.log('🚚 [ShipmentWizard] Context:', {
      projectId,
      scenarioId: currentScenario?.id || 'none',
      datasetId: currentDataset?.id || 'none',
      locationsCount: locations.length,
      validLocationsCount: validLocations.length,
      locationsLoading,
      hasScenario: !!currentScenario,
      hasDataset: !!currentDataset,
      // Skills debugging
      skillsResponse,
      skillsCount: skills.length,
      skillsLoading,
      skillsError,
      mappedSkills: skills
    });
  }, [projectId, currentScenario?.id, currentDataset?.id, locations.length, validLocations.length, locationsLoading, skillsResponse, skills, skillsLoading, skillsError]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    setValue,
    trigger
  } = useForm<ShipmentFormData>({
    resolver: zodResolver(shipmentValidationSchema),
    defaultValues: {
      shipment_reference: '',
      priority: 5,
      // Enhanced DateTime fields (default to tomorrow 8 AM - 6 PM)
      pickup_time_window_start_dt: (() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(8, 0, 0, 0);
        return tomorrow;
      })(),
      pickup_time_window_end_dt: (() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(18, 0, 0, 0);
        return tomorrow;
      })(),
      delivery_time_window_start_dt: (() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(8, 0, 0, 0);
        return tomorrow;
      })(),
      delivery_time_window_end_dt: (() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(18, 0, 0, 0);
        return tomorrow;
      })(),
      // Legacy time windows (backward compatibility)
      pickup_time_window_start: 480, // 8 AM
      pickup_time_window_end: 1080, // 6 PM
      delivery_time_window_start: 480,
      delivery_time_window_end: 1080,
      // Setup and Service Times (minutes)
      pickup_setup: 15, // Default 15 minutes setup time
      pickup_service: 30, // Default 30 minutes service time
      delivery_setup: 10, // Default 10 minutes setup time
      delivery_service: 20, // Default 20 minutes service time
      products: [],
      required_skills: [],
      is_direct_delivery: false,
      requires_same_vehicle: true,
      ...initialData
    },
    mode: 'onChange'
  });

  const watchedProducts = watch('products');
  const currentStepId = WIZARD_STEPS[activeStep].id;

  // Debug logging for products and skills loading
  useEffect(() => {
    console.log('🛍️ [ShipmentWizard] Real data loaded:', {
      productsCount: products.length,
      productsLoading,
      productsError: productsError?.message,
      skillsCount: skills.length,
      skillsLoading,
      skillsError: skillsError?.message,
      projectId
    });
  }, [products.length, productsLoading, productsError, skills.length, skillsLoading, skillsError, projectId]);

  const handleNext = async (event?: React.MouseEvent) => {
    // Prevent any form submission during navigation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    console.log('🚚 [ShipmentWizard] Next button clicked:', {
      currentStep: activeStep,
      currentStepId,
      targetStep: activeStep + 1
    });
    
    // Validate current step
    const fieldsToValidate = getStepFields(currentStepId);
    const isStepValid = await trigger(fieldsToValidate);
    
    if (isStepValid) {
      const nextStep = Math.min(activeStep + 1, WIZARD_STEPS.length - 1);
      console.log('✅ [ShipmentWizard] Moving to step:', nextStep);
      setActiveStep(nextStep);
    } else {
      console.log('❌ [ShipmentWizard] Step validation failed for:', currentStepId);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepClick = (step: number) => {
    // Allow navigation to completed steps
    if (step < activeStep) {
      setActiveStep(step);
    }
  };

  const getStepFields = (stepId: WizardStep): (keyof ShipmentFormData)[] => {
    switch (stepId) {
      case 'basic':
        return ['shipment_reference', 'description', 'priority'];
      case 'locations':
        return ['pickup_location_id', 'delivery_location_id'];
      case 'time_windows':
        return ['pickup_time_window_start', 'pickup_time_window_end', 
                'delivery_time_window_start', 'delivery_time_window_end',
                'pickup_setup', 'pickup_service', 'delivery_setup', 'delivery_service'];
      case 'products':
        return ['products'];
      case 'constraints':
        return ['is_direct_delivery', 'requires_same_vehicle', 'max_transit_time'];
      default:
        return [];
    }
  };

  const handleFormSubmit = async (data: ShipmentFormData) => {
    console.log('🚚 [ShipmentWizard] Form submit attempted:', {
      activeStep,
      currentStepId,
      isReviewStep: activeStep === WIZARD_STEPS.length - 1,
      submitted
    });
    
    // CRITICAL: Only submit if we're on the final review step and haven't already submitted
    if (activeStep !== WIZARD_STEPS.length - 1 || submitted) {
      console.log('🚫 [ShipmentWizard] Form submission blocked - not on review step or already submitted');
      return;
    }
    
    console.log('✅ [ShipmentWizard] Proceeding with shipment creation');
    setSaving(true);
    setSubmitted(true);
    try {
      const result = await onSave(data);
      console.log('✅ Shipment created successfully:', result);
      setCreatedShipment(result || data);
      setSuccess(true);
    } catch (error) {
      console.error('❌ Error saving shipment:', error);
      // Reset submitted state on error to allow retry
      setSubmitted(false);
    } finally {
      setSaving(false);
    }
  };

  const addProduct = (product: Product) => {
    const currentProducts = watchedProducts || [];
    if (!currentProducts.find(p => p.product_id === product.id)) {
      setValue('products', [
        ...currentProducts,
        {
          product_id: product.id,
          product,
          quantity: 1,
          loading_sequence: currentProducts.length + 1
        }
      ]);
    }
  };

  const removeProduct = (productId: number) => {
    const currentProducts = watchedProducts || [];
    setValue('products', currentProducts.filter(p => p.product_id !== productId));
  };

  // Skills management functions
  const addSkill = (skill: Skill) => {
    const currentSkills = watch('required_skills') || [];
    // Check if skill is already added
    if (currentSkills.some(s => s.skill_id === skill.id)) {
      return; // Already added
    }
    
    const newSkillRequirement: SkillRequirement = {
      skill_id: skill.id,
      skill: skill,
      minimum_proficiency_level: 5, // Default to medium proficiency
      is_mandatory: true,
      reasoning: ''
    };
    
    setValue('required_skills', [...currentSkills, newSkillRequirement]);
  };

  const removeSkill = (skillId: number) => {
    const currentSkills = watch('required_skills') || [];
    const updatedSkills = currentSkills.filter(s => s.skill_id !== skillId);
    setValue('required_skills', updatedSkills);
  };

  const updateSkillRequirement = (skillId: number, updates: Partial<SkillRequirement>) => {
    const currentSkills = watch('required_skills') || [];
    const updatedSkills = currentSkills.map(skill => 
      skill.skill_id === skillId ? { ...skill, ...updates } : skill
    );
    setValue('required_skills', updatedSkills);
  };

  // DateTime to minutes conversion utility (for backward compatibility)
  const dateTimeToMinutes = (date?: Date): number => {
    if (!date) return 480; // Default 8 AM
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return hours * 60 + minutes;
  };

  // Watch skills
  const watchedSkills = watch('required_skills') || [];

  const updateProductQuantity = (productId: number, quantity: number) => {
    const currentProducts = watchedProducts || [];
    setValue('products', currentProducts.map(p =>
      p.product_id === productId ? { ...p, quantity } : p
    ));
  };

  const calculateTotalCapacity = () => {
    let totalWeight = 0;
    let totalVolume = 0;
    let totalItems = 0;

    watchedProducts?.forEach(p => {
      if (p.product) {
        totalWeight += (p.product.weight_per_unit_kg || 0) * p.quantity;
        totalVolume += (p.product.volume_per_unit_m3 || 0) * p.quantity;
        totalItems += p.quantity;
      }
    });

    return { totalWeight, totalVolume, totalItems };
  };

  // Watch datetime fields and sync with legacy fields for backward compatibility
  const watchedPickupStartDt = watch('pickup_time_window_start_dt');
  const watchedPickupEndDt = watch('pickup_time_window_end_dt');
  const watchedDeliveryStartDt = watch('delivery_time_window_start_dt');
  const watchedDeliveryEndDt = watch('delivery_time_window_end_dt');

  useEffect(() => {
    if (watchedPickupStartDt) {
      setValue('pickup_time_window_start', dateTimeToMinutes(watchedPickupStartDt));
    }
  }, [watchedPickupStartDt, setValue]);

  useEffect(() => {
    if (watchedPickupEndDt) {
      setValue('pickup_time_window_end', dateTimeToMinutes(watchedPickupEndDt));
    }
  }, [watchedPickupEndDt, setValue]);

  useEffect(() => {
    if (watchedDeliveryStartDt) {
      setValue('delivery_time_window_start', dateTimeToMinutes(watchedDeliveryStartDt));
    }
  }, [watchedDeliveryStartDt, setValue]);

  useEffect(() => {
    if (watchedDeliveryEndDt) {
      setValue('delivery_time_window_end', dateTimeToMinutes(watchedDeliveryEndDt));
    }
  }, [watchedDeliveryEndDt, setValue]);

  // Show loading state if locations, products, or skills are loading
  if (locationsLoading || productsLoading || skillsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading shipment data...
        </Typography>
      </Box>
    );
  }

  // Show error state if there are critical errors
  if (productsError || skillsError) {
    return (
      <Box p={4}>
        <Alert severity="error">
          Error loading data: {productsError?.message || skillsError?.message}
          <Box sx={{ mt: 2 }}>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Show success confirmation when shipment is created
  if (success && createdShipment) {
    return (
      <Box>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            {/* Success Icon */}
            <Box sx={{ mb: 3 }}>
              <Check 
                sx={{ 
                  fontSize: 80, 
                  color: 'success.main',
                  backgroundColor: 'success.light',
                  borderRadius: '50%',
                  p: 2
                }} 
              />
            </Box>
            
            {/* Success Message */}
            <Typography variant="h4" gutterBottom color="success.main" sx={{ fontWeight: 'bold' }}>
              Shipment Created Successfully!
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mb: 4 }}>
              Your shipment "{createdShipment.shipment_reference || 'New Shipment'}" has been created and is ready for optimization.
            </Typography>
            
            {/* Success Details */}
            <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'left', maxWidth: 600, mx: 'auto' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                Shipment Details:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Reference:</strong> {createdShipment.shipment_reference || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Priority:</strong> {createdShipment.priority || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Products:</strong> {createdShipment.products?.length || 0} items
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Status:</strong> <Chip label="Ready for Optimization" color="success" size="small" />
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            
            {/* Action Buttons */}
            <Box display="flex" justifyContent="center" gap={2}>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  // Reset the wizard to create another shipment
                  setSuccess(false);
                  setCreatedShipment(null);
                  setSubmitted(false);
                  setActiveStep(0);
                  setSaving(false);
                  
                  // Reset the form to default values with enhanced fields
                  setValue('shipment_reference', '');
                  setValue('description', '');
                  setValue('priority', 5);
                  setValue('pickup_location_id', undefined);
                  setValue('delivery_location_id', undefined);
                  
                  // Reset enhanced datetime fields
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const defaultStart = new Date(tomorrow);
                  defaultStart.setHours(8, 0, 0, 0);
                  const defaultEnd = new Date(tomorrow);
                  defaultEnd.setHours(18, 0, 0, 0);
                  
                  setValue('pickup_time_window_start_dt', defaultStart);
                  setValue('pickup_time_window_end_dt', defaultEnd);
                  setValue('delivery_time_window_start_dt', defaultStart);
                  setValue('delivery_time_window_end_dt', defaultEnd);
                  
                  // Legacy time windows
                  setValue('pickup_time_window_start', 480); // 8 AM
                  setValue('pickup_time_window_end', 1080); // 6 PM
                  setValue('delivery_time_window_start', 480);
                  setValue('delivery_time_window_end', 1080);
                  
                  setValue('products', []);
                  setValue('required_skills', []); // Reset skills
                  setValue('is_direct_delivery', false);
                  setValue('requires_same_vehicle', true);
                  setValue('max_transit_time', null);
                  
                  console.log('✅ [ShipmentWizard] Wizard reset for new shipment');
                }}
                startIcon={<LocalShipping />}
              >
                Create Another Shipment
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  // Navigate back to dashboard or project page
                  if (projectId) {
                    navigate(`/projects/${projectId}`);
                  } else {
                    navigate('/dashboard');
                  }
                }}
                startIcon={<Check />}
              >
                Done
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {WIZARD_STEPS.map((step, index) => (
          <Step key={step.id} onClick={() => handleStepClick(index)} sx={{ cursor: index < activeStep ? 'pointer' : 'default' }}>
            <StepLabel
              icon={step.icon}
              error={currentStepId === step.id && Object.keys(errors).length > 0}
            >
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* MPAD Enhancement: Scenario/Dataset Context Display */}
      <Alert 
        severity="info" 
        icon={<Info />}
        sx={{ mb: 3, bgcolor: 'primary.50', borderLeft: 3, borderColor: 'primary.main' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            📋 Enhanced Context Integration (MPAD)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Chip 
              label={`Scenario: ${currentScenario?.name || 'None'}`}
              color={currentScenario ? 'primary' : 'default'}
              size="small"
              variant={currentScenario ? 'filled' : 'outlined'}
            />
            <Chip 
              label={`Dataset: ${currentDataset?.name || 'Default Dataset'}`}
              color={currentDataset ? 'secondary' : 'default'}
              size="small"
              variant={currentDataset ? 'filled' : 'outlined'}
            />
            <Chip 
              label={`Version: ${currentDataset?.version || 1}`}
              color="default"
              size="small"
              variant="outlined"
            />
            <Chip 
              label="✨ Enhanced DateTime & Skills Ready"
              color="success"
              size="small"
              variant="outlined"
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Shipments will be automatically associated with scenario context and support enhanced datetime time windows with timezone handling.
          </Typography>
        </Box>
      </Alert>

      <form onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only allow submission if we're on the review step
        if (activeStep === WIZARD_STEPS.length - 1) {
          handleSubmit(handleFormSubmit)(e);
        } else {
          console.log('🚫 [ShipmentWizard] Form submission prevented - not on review step');
        }
      }}>
        <Card>
          <CardContent>
            {/* Step 1: Basic Information */}
            {currentStepId === 'basic' && (
              <Box>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                  <Info />
                  Basic Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="shipment_reference"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Shipment Reference"
                          fullWidth
                          required
                          error={!!errors.shipment_reference}
                          helperText={errors.shipment_reference?.message}
                          placeholder="e.g., SHP-2024-001"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocalShipping />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="priority"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <Typography gutterBottom>
                            Priority Level: {field.value}
                          </Typography>
                          <Slider
                            {...field}
                            min={1}
                            max={10}
                            marks
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => {
                              if (value <= 3) return 'Low';
                              if (value <= 7) return 'Medium';
                              return 'High';
                            }}
                          />
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Description"
                          multiline
                          rows={3}
                          fullWidth
                          error={!!errors.description}
                          helperText={errors.description?.message}
                          placeholder="Brief description of the shipment..."
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Step 2: Locations */}
            {currentStepId === 'locations' && (
              <Box>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                  <Place />
                  Pickup & Delivery Locations
                </Typography>
                
                <Alert severity="info" sx={{ mb: 3 }}>
                  Select different locations for pickup and delivery to create the shipment route.
                  {!currentScenario && (
                    <Box sx={{ mt: 1 }}>
                      <strong>Note:</strong> Showing all locations. Select a scenario to filter locations by dataset.
                    </Box>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <strong>Requirement:</strong> Selected locations must have valid coordinates for shipment creation.
                  </Box>
                </Alert>

                {/* Coordinate validation warning */}
                {(() => {
                  const locationsWithoutCoords = locations.filter(l => !l.location_lon || !l.location_lat);
                  if (locationsWithoutCoords.length > 0) {
                    return (
                      <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="subtitle2">Locations Missing Coordinates</Typography>
                        <Typography variant="body2">
                          {locationsWithoutCoords.length} location(s) are missing coordinates and cannot be used for shipments.
                          These locations will be filtered out of the selection.
                        </Typography>
                      </Alert>
                    );
                  }
                  return null;
                })()}
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="pickup_location_id"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          fullWidth
                          size="small"
                          options={validLocations}
                          loading={locationsLoading}
                          noOptionsText={locationsLoading ? "Loading locations..." : validLocations.length === 0 ? "No locations with valid coordinates found" : "No locations found"}
                          sx={{
                            minWidth: '400px !important',
                            '& .MuiAutocomplete-inputRoot': {
                              minHeight: '40px !important',
                              fontSize: '0.875rem !important',
                              minWidth: '400px !important'
                            },
                            '& .MuiAutocomplete-input': {
                              minWidth: '350px !important'
                            },
                            '& .MuiAutocomplete-option': {
                              fontSize: '0.875rem !important',
                              padding: '8px 12px !important',
                              whiteSpace: 'nowrap !important',
                              overflow: 'hidden !important',
                              textOverflow: 'ellipsis !important',
                              minWidth: '350px !important'
                            },
                            '& .MuiInputBase-root': {
                              paddingRight: '14px !important',
                              minWidth: '400px !important'
                            },
                            '& .MuiOutlinedInput-root': {
                              minWidth: '400px !important'
                            }
                          }}
                          ListboxProps={{
                            sx: {
                              minWidth: '500px !important',
                              maxWidth: '700px !important',
                              width: '500px !important'
                            }
                          }}
                          getOptionLabel={(option) => {
                            if (option.address) {
                              return `${option.name} - ${option.address}`;
                            }
                            // Show coordinates as fallback for better identification
                            if (option.location_lon && option.location_lat) {
                              return `Location ${option.name} (${option.location_lat?.toFixed(3)}, ${option.location_lon?.toFixed(3)})`;
                            }
                            return `Location ${option.name}`;
                          }}
                          value={validLocations.find(l => l.id === field.value) || null}
                          onChange={(_, value) => field.onChange(value?.id)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Pickup Location"
                              required
                              size="small"
                              error={!!errors.pickup_location_id}
                              helperText={errors.pickup_location_id?.message}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Place color="primary" />
                                  </InputAdornment>
                                )
                              }}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="delivery_location_id"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          fullWidth
                          size="small"
                          options={validLocations}
                          loading={locationsLoading}
                          noOptionsText={locationsLoading ? "Loading locations..." : validLocations.length === 0 ? "No locations with valid coordinates found" : "No locations found"}
                          sx={{
                            minWidth: '400px !important',
                            '& .MuiAutocomplete-inputRoot': {
                              minHeight: '40px !important',
                              fontSize: '0.875rem !important',
                              minWidth: '400px !important'
                            },
                            '& .MuiAutocomplete-input': {
                              minWidth: '350px !important'
                            },
                            '& .MuiAutocomplete-option': {
                              fontSize: '0.875rem !important',
                              padding: '8px 12px !important',
                              whiteSpace: 'nowrap !important',
                              overflow: 'hidden !important',
                              textOverflow: 'ellipsis !important',
                              minWidth: '350px !important'
                            },
                            '& .MuiInputBase-root': {
                              paddingRight: '14px !important',
                              minWidth: '400px !important'
                            },
                            '& .MuiOutlinedInput-root': {
                              minWidth: '400px !important'
                            }
                          }}
                          ListboxProps={{
                            sx: {
                              minWidth: '500px !important',
                              maxWidth: '700px !important',
                              width: '500px !important'
                            }
                          }}
                          getOptionLabel={(option) => {
                            if (option.address) {
                              return `${option.name} - ${option.address}`;
                            }
                            // Show coordinates as fallback for better identification
                            if (option.location_lon && option.location_lat) {
                              return `Location ${option.name} (${option.location_lat?.toFixed(3)}, ${option.location_lon?.toFixed(3)})`;
                            }
                            return `Location ${option.name}`;
                          }}
                          value={validLocations.find(l => l.id === field.value) || null}
                          onChange={(_, value) => field.onChange(value?.id)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Delivery Location"
                              required
                              size="small"
                              error={!!errors.delivery_location_id}
                              helperText={errors.delivery_location_id?.message}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Place color="secondary" />
                                  </InputAdornment>
                                )
                              }}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Map visualization would go here */}
              </Box>
            )}

            {/* Step 3: Time Windows */}
            {currentStepId === 'time_windows' && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                    <Schedule />
                    📅 Enhanced Date & Time Windows
                  </Typography>
                  
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      <strong>MPAD Enhancement:</strong> Select specific dates and times for pickup and delivery windows. 
                      This replaces the previous time-only sliders with full date+time selection capabilities.
                    </Typography>
                  </Alert>
                  
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                        <AccessTime color="primary" />
                        Pickup Time Window
                      </Typography>
                      
                      <Stack spacing={2}>
                        <Controller
                          name="pickup_time_window_start_dt"
                          control={control}
                          render={({ field }) => (
                            <DateTimePicker
                              label="Pickup Start Date & Time"
                              value={field.value}
                              onChange={(newValue) => field.onChange(newValue)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: !!errors.pickup_time_window_start_dt,
                                  helperText: errors.pickup_time_window_start_dt?.message
                                }
                              }}
                            />
                          )}
                        />

                        <Controller
                          name="pickup_time_window_end_dt"
                          control={control}
                          render={({ field }) => (
                            <DateTimePicker
                              label="Pickup End Date & Time"
                              value={field.value}
                              onChange={(newValue) => field.onChange(newValue)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: !!errors.pickup_time_window_end_dt,
                                  helperText: errors.pickup_time_window_end_dt?.message
                                }
                              }}
                            />
                          )}
                        />
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                        <AccessTime color="secondary" />
                        Delivery Time Window
                      </Typography>
                      
                      <Stack spacing={2}>
                        <Controller
                          name="delivery_time_window_start_dt"
                          control={control}
                          render={({ field }) => (
                            <DateTimePicker
                              label="Delivery Start Date & Time"
                              value={field.value}
                              onChange={(newValue) => field.onChange(newValue)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: !!errors.delivery_time_window_start_dt,
                                  helperText: errors.delivery_time_window_start_dt?.message
                                }
                              }}
                            />
                          )}
                        />

                        <Controller
                          name="delivery_time_window_end_dt"
                          control={control}
                          render={({ field }) => (
                            <DateTimePicker
                              label="Delivery End Date & Time"
                              value={field.value}
                              onChange={(newValue) => field.onChange(newValue)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: !!errors.delivery_time_window_end_dt,
                                  helperText: errors.delivery_time_window_end_dt?.message
                                }
                              }}
                            />
                          )}
                        />
                      </Stack>
                    </Grid>
                  </Grid>

                  {/* Setup and Service Times Section */}
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                    <AccessTime />
                    Operational Times
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Configure the time required for setup (preparation, paperwork) and service (loading/unloading) at each location.
                  </Typography>

                  <Grid container spacing={3}>
                    {/* Pickup Operational Times */}
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom color="primary">
                          Pickup Operational Times
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Controller
                              name="pickup_setup"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Setup Time"
                                  type="number"
                                  fullWidth
                                  inputProps={{ min: 0, max: 480 }}
                                  InputProps={{
                                    endAdornment: <Typography variant="body2" color="text.secondary">min</Typography>
                                  }}
                                  error={!!errors.pickup_setup}
                                  helperText={errors.pickup_setup?.message || "Fixed admin time (paperwork, preparation)"}
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Controller
                              name="pickup_service"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Service Time"
                                  type="number"
                                  fullWidth
                                  inputProps={{ min: 0, max: 480 }}
                                  InputProps={{
                                    endAdornment: <Typography variant="body2" color="text.secondary">min</Typography>
                                  }}
                                  error={!!errors.pickup_service}
                                  helperText={errors.pickup_service?.message || "Time required for loading operations"}
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>

                    {/* Delivery Operational Times */}
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom color="primary">
                          Delivery Operational Times
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Controller
                              name="delivery_setup"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Setup Time"
                                  type="number"
                                  fullWidth
                                  inputProps={{ min: 0, max: 480 }}
                                  InputProps={{
                                    endAdornment: <Typography variant="body2" color="text.secondary">min</Typography>
                                  }}
                                  error={!!errors.delivery_setup}
                                  helperText={errors.delivery_setup?.message || "Fixed admin time (paperwork, preparation)"}
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Controller
                              name="delivery_service"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Service Time"
                                  type="number"
                                  fullWidth
                                  inputProps={{ min: 0, max: 480 }}
                                  InputProps={{
                                    endAdornment: <Typography variant="body2" color="text.secondary">min</Typography>
                                  }}
                                  error={!!errors.delivery_service}
                                  helperText={errors.delivery_service?.message || "Time required for unloading operations"}
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Show converted legacy time values for debugging/compatibility */}
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Legacy Compatibility:</strong> Pickup ({formatTimeWindow(watch('pickup_time_window_start'))} - {formatTimeWindow(watch('pickup_time_window_end'))}), 
                      Delivery ({formatTimeWindow(watch('delivery_time_window_start'))} - {formatTimeWindow(watch('delivery_time_window_end'))})
                    </Typography>
                  </Box>

                  {(errors.pickup_time_window_end_dt || errors.delivery_time_window_end_dt) && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      Please ensure end times are after start times for both pickup and delivery windows.
                    </Alert>
                  )}
                </Box>
              </LocalizationProvider>
            )}

            {/* Step 4: Products */}
            {currentStepId === 'products' && (
              <Box>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                  <Inventory />
                  Products
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={products}
                      getOptionLabel={(option) => `${option.name} (${option.category})`}
                      sx={{
                        minWidth: '400px !important',
                        '& .MuiAutocomplete-inputRoot': {
                          minHeight: '40px !important',
                          fontSize: '0.875rem !important',
                          minWidth: '400px !important'
                        },
                        '& .MuiAutocomplete-input': {
                          minWidth: '350px !important'
                        },
                        '& .MuiAutocomplete-option': {
                          fontSize: '0.875rem !important',
                          padding: '8px 12px !important',
                          whiteSpace: 'nowrap !important',
                          overflow: 'hidden !important',
                          textOverflow: 'ellipsis !important',
                          minWidth: '350px !important'
                        },
                        '& .MuiInputBase-root': {
                          paddingRight: '14px !important',
                          minWidth: '400px !important'
                        },
                        '& .MuiOutlinedInput-root': {
                          minWidth: '400px !important'
                        }
                      }}
                      ListboxProps={{
                        sx: {
                          minWidth: '500px !important',
                          maxWidth: '700px !important',
                          width: '500px !important'
                        }
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box display="flex" alignItems="center" gap={1} width="100%">
                            <Box flex={1}>
                              <Typography variant="body1">{option.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.category}
                                {option.requires_refrigeration && ' • Refrigerated'}
                                {option.hazmat_class && ` • Hazmat ${option.hazmat_class}`}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Add Product"
                          placeholder="Search products..."
                          size="small"
                        />
                      )}
                      onChange={(_, value) => {
                        if (value) {
                          addProduct(value);
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                {watchedProducts.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell align="center">Quantity</TableCell>
                          <TableCell align="center">Sequence</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {watchedProducts.map((productAssignment, index) => (
                          <TableRow key={productAssignment.product_id}>
                            <TableCell>
                              <Typography variant="body2">
                                {productAssignment.product?.name}
                              </Typography>
                              {productAssignment.product?.requires_refrigeration && (
                                <Chip label="Refrigerated" size="small" color="info" />
                              )}
                            </TableCell>
                            <TableCell>{productAssignment.product?.category}</TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={productAssignment.quantity}
                                onChange={(e) => updateProductQuantity(
                                  productAssignment.product_id,
                                  parseFloat(e.target.value) || 1
                                )}
                                inputProps={{ min: 0.01, step: 0.01 }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="center">{index + 1}</TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeProduct(productAssignment.product_id)}
                              >
                                <Warning />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="warning">
                    No products added. Add at least one product to continue.
                  </Alert>
                )}

                {/* Capacity Summary */}
                {watchedProducts.length > 0 && (
                  <Box mt={3} p={2} bgcolor="grey.100" borderRadius={1}>
                    <Typography variant="subtitle2" gutterBottom>
                      Capacity Summary:
                    </Typography>
                    {(() => {
                      const { totalWeight, totalVolume, totalItems } = calculateTotalCapacity();
                      return (
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <Typography variant="body2">
                              Weight: {totalWeight.toFixed(2)} kg
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2">
                              Volume: {totalVolume.toFixed(3)} m³
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2">
                              Items: {totalItems}
                            </Typography>
                          </Grid>
                        </Grid>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            )}

            {/* Step 5: Skills & Requirements */}
            {currentStepId === 'skills' && (
              <Box>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                  <Speed />
                  Skills & Requirements
                </Typography>
                
                <Alert severity="info" sx={{ mb: 3 }}>
                  Select the skills required for this shipment. Skills help ensure the right vehicles are assigned.
                </Alert>
                
                <Grid container spacing={3}>
                  {/* Skills Selection */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={skills}
                      getOptionLabel={(option) => option.name}
                      value={watchedSkills.map(s => s.skill).filter(Boolean)}
                      onChange={(_, newValue) => {
                        // Clear existing skills and add new ones
                        setValue('required_skills', []);
                        newValue.forEach(skill => addSkill(skill));
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      loading={skillsLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Required Skills"
                          placeholder={skillsLoading ? "Loading skills..." : "Select skills required for this shipment"}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {skillsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {option.name}
                            </Typography>
                            {option.category && (
                              <Typography variant="caption" color="text.secondary">
                                {option.category}
                                {option.subcategory && ` • ${option.subcategory}`}
                              </Typography>
                            )}
                            {option.description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {option.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option.name}
                            {...getTagProps({ index })}
                            key={option.id}
                          />
                        ))
                      }
                    />
                  </Grid>
                  
                  {/* Selected Skills Configuration */}
                  {watchedSkills.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Skill Requirements Configuration
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Skill</TableCell>
                              <TableCell>Category</TableCell>
                              <TableCell align="center">Min. Proficiency (1-10)</TableCell>
                              <TableCell align="center">Mandatory</TableCell>
                              <TableCell>Reasoning</TableCell>
                              <TableCell align="center">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {watchedSkills.map((skillReq) => (
                              <TableRow key={skillReq.skill_id}>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      {skillReq.skill?.name}
                                    </Typography>
                                    {skillReq.skill?.description && (
                                      <Typography variant="caption" color="text.secondary">
                                        {skillReq.skill.description}
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={skillReq.skill?.category || 'General'} 
                                    size="small" 
                                    color="default" 
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={skillReq.minimum_proficiency_level}
                                    onChange={(e) => updateSkillRequirement(
                                      skillReq.skill_id,
                                      { minimum_proficiency_level: parseInt(e.target.value) || 1 }
                                    )}
                                    inputProps={{ min: 1, max: 10, step: 1 }}
                                    sx={{ width: 80 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Switch
                                    checked={skillReq.is_mandatory}
                                    onChange={(e) => updateSkillRequirement(
                                      skillReq.skill_id,
                                      { is_mandatory: e.target.checked }
                                    )}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    value={skillReq.reasoning || ''}
                                    onChange={(e) => updateSkillRequirement(
                                      skillReq.skill_id,
                                      { reasoning: e.target.value }
                                    )}
                                    placeholder="Why is this skill needed?"
                                    sx={{ minWidth: 200 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => removeSkill(skillReq.skill_id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  )}
                  
                  {/* Skills Summary */}
                  {watchedSkills.length === 0 && (
                    <Grid item xs={12}>
                      <Alert severity="warning">
                        No skills selected. This shipment can be handled by any available vehicle.
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Step 6: Constraints */}
            {currentStepId === 'constraints' && (
              <Box>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                  <DirectionsCar />
                  Shipment Constraints
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Controller
                      name="is_direct_delivery"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              {...field}
                              checked={field.value}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1">Direct Delivery</Typography>
                              <Typography variant="caption" color="text.secondary">
                                No other stops between pickup and delivery
                              </Typography>
                            </Box>
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="requires_same_vehicle"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              {...field}
                              checked={field.value}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1">Same Vehicle Required</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Pickup and delivery must use the same vehicle
                              </Typography>
                            </Box>
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="max_transit_time"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                          label="Maximum Transit Time"
                          type="number"
                          fullWidth
                          helperText="Maximum time allowed between pickup and delivery"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                            startAdornment: (
                              <InputAdornment position="start">
                                <Speed />
                              </InputAdornment>
                            )
                          }}
                          inputProps={{ min: 1 }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Step 7: Review */}
            {currentStepId === 'review' && (
              <Box>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                  <Check />
                  Review Shipment
                </Typography>
                
                <Grid container spacing={3}>
                  {/* Basic Info */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom color="primary">
                        Basic Information
                      </Typography>
                      <Typography variant="body2">
                        <strong>Reference:</strong> {watch('shipment_reference')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Priority:</strong> {watch('priority')}
                      </Typography>
                      {watch('description') && (
                        <Typography variant="body2">
                          <strong>Description:</strong> {watch('description')}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>

                  {/* Locations */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom color="primary">
                        Locations
                      </Typography>
                      <Typography variant="body2">
                        <strong>Pickup:</strong> {
                          validLocations.find(l => l.id === watch('pickup_location_id'))?.name
                        }
                      </Typography>
                      <Typography variant="body2">
                        <strong>Delivery:</strong> {
                          validLocations.find(l => l.id === watch('delivery_location_id'))?.name
                        }
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Enhanced Time Windows */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom color="primary">
                        📅 Enhanced Time Windows
                      </Typography>
                      <Typography variant="body2">
                        <strong>Pickup:</strong> {watch('pickup_time_window_start_dt')?.toLocaleDateString()} {watch('pickup_time_window_start_dt')?.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {watch('pickup_time_window_end_dt')?.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Delivery:</strong> {watch('delivery_time_window_start_dt')?.toLocaleDateString()} {watch('delivery_time_window_start_dt')?.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {watch('delivery_time_window_end_dt')?.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Legacy: Pickup ({formatTimeWindow(watch('pickup_time_window_start'))} - {formatTimeWindow(watch('pickup_time_window_end'))}), Delivery ({formatTimeWindow(watch('delivery_time_window_start'))} - {formatTimeWindow(watch('delivery_time_window_end'))})
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Setup and Service Times */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom color="primary">
                        ⏱️ Setup & Service Times
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                            Pickup Times:
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            <strong>Setup:</strong> {watch('pickup_setup')} minutes
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            <strong>Service:</strong> {watch('pickup_service')} minutes
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2, fontWeight: 'medium', color: 'primary.main' }}>
                            <strong>Total Pickup Time:</strong> {(watch('pickup_setup') + watch('pickup_service'))} minutes
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                            Delivery Times:
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            <strong>Setup:</strong> {watch('delivery_setup')} minutes
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            <strong>Service:</strong> {watch('delivery_service')} minutes
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2, fontWeight: 'medium', color: 'primary.main' }}>
                            <strong>Total Delivery Time:</strong> {(watch('delivery_setup') + watch('delivery_service'))} minutes
                          </Typography>
                        </Grid>
                      </Grid>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <strong>Note:</strong> Setup time includes preparation and paperwork. Service time covers actual loading/unloading operations. 
                        Total operational time at each location: {(watch('pickup_setup') + watch('pickup_service') + watch('delivery_setup') + watch('delivery_service'))} minutes.
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Products & Capacity */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom color="primary">
                        Products & Capacity
                      </Typography>
                      <Typography variant="body2">
                        <strong>Products:</strong> {watchedProducts.length} items
                      </Typography>
                      {(() => {
                        const { totalWeight, totalVolume } = calculateTotalCapacity();
                        return (
                          <>
                            <Typography variant="body2">
                              <strong>Total Weight:</strong> {totalWeight.toFixed(2)} kg
                            </Typography>
                            <Typography variant="body2">
                              <strong>Total Volume:</strong> {totalVolume.toFixed(3)} m³
                            </Typography>
                          </>
                        );
                      })()}
                    </Paper>
                  </Grid>

                  {/* Skills & Requirements */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom color="primary">
                        🎧 Skills & Requirements
                      </Typography>
                      {watchedSkills.length > 0 ? (
                        <>
                          <Typography variant="body2">
                            <strong>Required Skills:</strong> {watchedSkills.length} skills
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                            {watchedSkills.map((skill) => (
                              <Chip
                                key={skill.skill_id}
                                label={`${skill.skill?.name} (L${skill.minimum_proficiency_level})`}
                                color={skill.is_mandatory ? 'primary' : 'default'}
                                size="small"
                              />
                            ))}
                          </Box>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No specific skills required
                        </Typography>
                      )}
                    </Paper>
                  </Grid>

                  {/* Constraints */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom color="primary">
                        Constraints
                      </Typography>
                      <Box display="flex" gap={2}>
                        {watch('is_direct_delivery') && (
                          <Chip label="Direct Delivery" color="primary" size="small" />
                        )}
                        {watch('requires_same_vehicle') && (
                          <Chip label="Same Vehicle Required" color="primary" size="small" />
                        )}
                        {watch('max_transit_time') && (
                          <Chip 
                            label={`Max Transit: ${watch('max_transit_time')} min`} 
                            color="secondary" 
                            size="small" 
                          />
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>

          <Divider />

          {/* Navigation Buttons */}
          <Box p={2} display="flex" justifyContent="space-between">
            <Button
              type="button"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </Button>

            <Box display="flex" gap={2}>
              <Button
                type="button"
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<NavigateBefore />}
              >
                Back
              </Button>

              {activeStep === WIZARD_STEPS.length - 1 ? (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!isValid || saving || submitted}
                  startIcon={saving ? <CircularProgress size={20} /> : submitted ? <Check /> : <Check />}
                >
                  {submitted ? 'Created!' : saving ? 'Creating...' : 'Create Shipment'}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="contained"
                  onClick={(e) => handleNext(e)}
                  endIcon={<NavigateNext />}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Card>
      </form>
    </Box>
  );
};

export default ShipmentWizard;